/**
 * Razorpay Checkout Utility
 * Handles loading the Razorpay script and opening the checkout popup.
 */

let razorpayScriptLoaded = false;

export function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (razorpayScriptLoaded || window.Razorpay) {
      razorpayScriptLoaded = true;
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => {
      razorpayScriptLoaded = true;
      resolve(true);
    };
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

/**
 * Open Razorpay checkout popup for an order.
 *
 * @param {Object} options
 * @param {string} options.razorpayKeyId - Razorpay key_id from backend
 * @param {number} options.amount - Amount in paise (e.g. 30000 for ₹300)
 * @param {string} options.currency - Currency code (default 'INR')
 * @param {string} options.orderId - Razorpay order_id from backend
 * @param {string} [options.subscriptionId] - Razorpay subscription_id (for subscription flow)
 * @param {string} options.customerName - Customer's name
 * @param {string} options.customerEmail - Customer's email
 * @param {Object} options.notes - Notes to attach to payment
 * @param {string} [options.description] - Payment description
 * @param {Function} options.onSuccess - Called with { razorpay_order_id, razorpay_payment_id, razorpay_signature }
 * @param {Function} options.onFailure - Called with error response
 * @param {Function} [options.onDismiss] - Called when user closes modal without paying
 */
export async function openRazorpayCheckout({
  razorpayKeyId,
  amount,
  currency = 'INR',
  orderId,
  subscriptionId,
  customerName,
  customerEmail,
  notes = {},
  description = 'TeamGrid Subscription',
  onSuccess,
  onFailure,
  onDismiss,
}) {
  const loaded = await loadRazorpayScript();
  if (!loaded) {
    onFailure?.({ error: 'Failed to load Razorpay SDK' });
    return;
  }

  const options = {
    key: razorpayKeyId,
    currency,
    name: 'TeamGrid',
    description,
    handler: function (response) {
      onSuccess?.(response);
    },
    prefill: {
      name: customerName,
      email: customerEmail,
    },
    notes,
    theme: {
      color: '#6366f1',
    },
    modal: {
      ondismiss: function () {
        onDismiss?.();
      },
    },
  };

  if (subscriptionId) {
    options.subscription_id = subscriptionId;
  } else {
    options.amount = amount;
    options.order_id = orderId;
  }

  const rzp = new window.Razorpay(options);

  rzp.on('payment.failed', function (response) {
    onFailure?.(response.error);
  });

  rzp.open();
}

/**
 * Detect payment gateway based on user's IP.
 * Calls backend endpoint to determine gateway.
 * Falls back to 'stripe' if detection fails.
 */
export async function detectPaymentGateway(apiInstance) {
  try {
    const response = await apiInstance.get('/api/v1/billing/profile');
    const gateway = response.data?.data?.paymentGateway;
    if (gateway === 'razorpay') return 'razorpay';
    return 'stripe';
  } catch {
    return 'stripe';
  }
}

/**
 * Check if gateway is Razorpay from billing profile data
 */
export function isRazorpayGateway(billingData) {
  return billingData?.paymentGateway === 'razorpay';
}
